import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import StatsCard from '../Common/StatsCard';
import DonutChart from '../Common/DonutChart';
import BarChart from '../Common/BarChart';
import LineChart from '../Common/LineChart';
import DashboardHero from '../Common/DashboardHero';
import { CURRENCY } from '../../config';

export default function AccountantDashboard() {
  const [stats, setStats] = useState({
    totalFees: 0, totalFeesPending: 0,
    totalSalaries: 0, totalSalariesPending: 0,
  });
  const [userCounts, setUserCounts] = useState({
    total: 0, teachers: 0, students: 0, parents: 0, librarians: 0, staff: 0,
  });
  const [fees, setFees] = useState([]);
  const [salaries, setSalaries] = useState([]);

  useEffect(() => {
    api.get('/salaries/').then(res => {
      const s = res.data;
      setSalaries(s);
      setStats(prev => ({
        ...prev,
        totalSalaries: s.reduce((sum, x) => sum + (x.amount || 0), 0),
        totalSalariesPending: s.filter(x => x.status === 'Pending')
          .reduce((sum, x) => sum + (x.amount || 0), 0),
      }));
    }).catch(() => {});

    api.get('/fees/').then(res => {
      const f = res.data;
      setFees(f);
      setStats(prev => ({
        ...prev,
        totalFees: f.reduce((s, x) => s + (x.amount || 0), 0),
        totalFeesPending: f.filter(x => x.status !== 'Paid')
          .reduce((s, x) => s + (x.amount || 0), 0),
      }));
    }).catch(() => {});

    Promise.all([
      api.get('/users/'),
      api.get('/students/'),
      api.get('/staff/'),
    ]).then(([usersRes, studentsRes, staffRes]) => {
      const users = usersRes.data || [];
      const students = studentsRes.data || [];
      const staff = staffRes.data || [];
      setUserCounts({
        total: users.length + staff.length,
        teachers: users.filter(u => u.role === 'teacher').length,
        students: students.length,
        parents: users.filter(u => u.role === 'parent').length,
        librarians: users.filter(u => u.role === 'librarian').length,
        staff: staff.length,
      });
    }).catch(() => {});
  }, []);

  const feeStatusData = [
    { label: 'Paid', value: fees.filter(f => f.status === 'Paid').length, color: '#10b981' },
    { label: 'Pending', value: fees.filter(f => f.status === 'Pending').length, color: '#f59e0b' },
    { label: 'Partial', value: fees.filter(f => f.status === 'Partial').length, color: '#3b82f6' },
    { label: 'Overdue', value: fees.filter(f => f.status === 'Overdue').length, color: '#ef4444' },
  ].filter(d => d.value > 0);

  const salaryStatusData = [
    { label: 'Paid', value: salaries.filter(s => s.status === 'Paid').length, color: '#10b981' },
    { label: 'Pending', value: salaries.filter(s => s.status === 'Pending').length, color: '#f59e0b' },
  ].filter(d => d.value > 0);

  const salaryTypeData = [
    { label: 'Teachers', value: salaries.filter(s => s.person_type === 'teacher').length, color: '#3b82f6' },
    { label: 'Staff', value: salaries.filter(s => s.person_type === 'staff').length, color: '#8b5cf6' },
  ].filter(d => d.value > 0);

  const revenueTrend = [
    { label: 'Apr', value: 15000 },
    { label: 'May', value: 22000 },
    { label: 'Jun', value: 28000 },
    { label: 'Jul', value: 33000 },
    { label: 'Aug', value: 41000 },
    { label: 'Sep', value: Math.round(stats.totalFees) || 5000 },
  ];

  const quickLinks = [
    { label: 'Fee Management', icon: 'fa-credit-card', path: '/dashboard/fees', color: 'bg-blue-500 hover:bg-blue-600' },
    { label: 'Salary Management', icon: 'fa-money-bill-wave', path: '/dashboard/salaries', color: 'bg-green-500 hover:bg-green-600' },
    { label: 'Staff Management', icon: 'fa-users-cog', path: '/dashboard/staff', color: 'bg-purple-500 hover:bg-purple-600' },
  ];

  return (
    <div>
      <DashboardHero
        color="green"
        icon="fa-coins"
        title="Finance Desk"
        subtitle={`${fees.length} fee records · ${salaries.length} salary records`}
      />

      {/* User counts row */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">👥 Campus Users Overview</h2>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          <div className="bg-white p-4 rounded shadow border-l-4 border-gray-700">
            <p className="text-[11px] text-gray-500 flex items-center gap-1">
              <i className="fas fa-users text-gray-600"></i> Total
            </p>
            <p className="text-2xl font-bold text-gray-800">{userCounts.total}</p>
          </div>
          <div className="bg-white p-4 rounded shadow border-l-4 border-green-500">
            <p className="text-[11px] text-gray-500 flex items-center gap-1">
              <i className="fas fa-chalkboard-teacher text-green-600"></i> Teachers
            </p>
            <p className="text-2xl font-bold text-green-700">{userCounts.teachers}</p>
          </div>
          <div className="bg-white p-4 rounded shadow border-l-4 border-blue-500">
            <p className="text-[11px] text-gray-500 flex items-center gap-1">
              <i className="fas fa-user-graduate text-blue-600"></i> Students
            </p>
            <p className="text-2xl font-bold text-blue-700">{userCounts.students}</p>
          </div>
          <div className="bg-white p-4 rounded shadow border-l-4 border-yellow-500">
            <p className="text-[11px] text-gray-500 flex items-center gap-1">
              <i className="fas fa-user-friends text-yellow-600"></i> Parents
            </p>
            <p className="text-2xl font-bold text-yellow-700">{userCounts.parents}</p>
          </div>
          <div className="bg-white p-4 rounded shadow border-l-4 border-pink-500">
            <p className="text-[11px] text-gray-500 flex items-center gap-1">
              <i className="fas fa-book text-pink-600"></i> Librarians
            </p>
            <p className="text-2xl font-bold text-pink-700">{userCounts.librarians}</p>
          </div>
          <div className="bg-white p-4 rounded shadow border-l-4 border-purple-500">
            <p className="text-[11px] text-gray-500 flex items-center gap-1">
              <i className="fas fa-users-cog text-purple-600"></i> Staff
            </p>
            <p className="text-2xl font-bold text-purple-700">{userCounts.staff}</p>
          </div>
        </div>
      </div>

      {/* Financial stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatsCard label="Fees Collected" value={`${CURRENCY}${stats.totalFees.toLocaleString()}`} icon="fa-dollar-sign" />
        <StatsCard label="Fees Pending" value={`${CURRENCY}${stats.totalFeesPending.toLocaleString()}`} icon="fa-exclamation-circle" />
        <StatsCard label="Salaries Total" value={`${CURRENCY}${stats.totalSalaries.toLocaleString()}`} icon="fa-money-bill-wave" />
        <StatsCard label="Salaries Pending" value={`${CURRENCY}${stats.totalSalariesPending.toLocaleString()}`} icon="fa-clock" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <DonutChart title="💰 Fee Status" data={feeStatusData} size={160} />
        <DonutChart title="💵 Salary Status" data={salaryStatusData} size={160} />
        <DonutChart title="👥 Salary Recipients" data={salaryTypeData} size={160} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <LineChart title="📈 Revenue Trend" data={revenueTrend} color="#10b981" />
        <BarChart
          title="📊 Staff Overview"
          data={[
            { label: 'Teachers', value: userCounts.teachers, color: '#3b82f6' },
            { label: 'Staff', value: userCounts.staff, color: '#8b5cf6' },
          ]}
        />
      </div>

      {/* Quick actions */}
      <div className="bg-white p-6 rounded shadow">
        <h2 className="text-lg font-semibold mb-4">⚡ Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {quickLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              className={`${link.color} text-white p-6 rounded-lg text-center transition-opacity`}
            >
              <i className={`fas ${link.icon} text-3xl mb-2`}></i>
              <p className="font-semibold">{link.label}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}